import { SubmitHandler } from 'react-hook-form'
import { DepositInputValues } from '../types'
import { notify } from '@utils/notifications'
import { RpcContext } from '@realms-today/spl-governance'
import { getProgramVersionForRealm } from '@models/registry/api'
import BN from 'bn.js'
import { BigNumber } from 'bignumber.js'
import { voteRegistryDepositWithoutLockup } from '../../../../actions/voteRegistryDepositWithoutLockup'
import queryClient from '@hooks/queries/queryClient'
import { tokenAccountQueryKeys } from '@hooks/queries/tokenAccount'
import { useConnection } from '@solana/wallet-adapter-react'
import useWalletOnePointOh from '@hooks/useWalletOnePointOh'
import useRealm from '@hooks/useRealm'
import { useUserCommunityTokenOwnerRecord } from '@hooks/queries/tokenOwnerRecord'
import useVotePluginsClientStore from '../../../../../stores/useVotePluginsClientStore'
import useDepositStore from '../../../../stores/useDepositStore'
import { useRealmQuery } from '@hooks/queries/realm'
import { useMintInfoByPubkeyQuery } from '@hooks/queries/mintInfo'
import { Dispatch, SetStateAction, useState } from 'react'

export const useDepositInDao = (
  setIsOpen: Dispatch<SetStateAction<boolean>>
) => {
  const realm = useRealmQuery().data?.result
  const depositMint = realm?.account.communityMint

  const depositMintInfo = useMintInfoByPubkeyQuery(depositMint).data?.result

  const { connection } = useConnection()
  const endpoint = connection.rpcEndpoint
  const wallet = useWalletOnePointOh()
  const { realmInfo, realmTokenAccount } = useRealm()
  const currentTokenOwnerRecord = useUserCommunityTokenOwnerRecord().data
    ?.result

  const client = useVotePluginsClientStore((s) => s.state.vsrClient)
  const { getOwnedDeposits } = useDepositStore()

  const [isLoading, setIsLoading] = useState(false)

  const depositTokens: SubmitHandler<DepositInputValues> = async (data) => {
    const amount = data.amount

    if (!amount) {
      notify({
        type: 'error',
        message: 'Invalid amount',
      })
      throw new Error('Invalid amount')
    }
    if (!wallet || !wallet.publicKey) {
      notify({
        type: 'error',
        message: 'Failed to get user’s wallet',
      })
      throw new Error('Failed to get user’s wallet')
    }
    if (!realm || !realmInfo || !realmTokenAccount) {
      notify({
        type: 'error',
        message: 'Failed to fetch realm',
      })
      throw new Error('Failed to fetch realm')
    }
    if (!depositMintInfo) {
      notify({
        type: 'error',
        message: 'Failed to fetch mint info',
      })
      throw new Error('Failed to fetch mint info')
    }
    if (!client) {
      notify({
        type: 'error',
        message: 'No VSR client found',
      })
      throw new Error('No VSR client found')
    }

    setIsLoading(true)

    const tokenOwnerRecordPk =
      typeof currentTokenOwnerRecord !== 'undefined'
        ? currentTokenOwnerRecord.pubkey
        : null
    const rpcContext = new RpcContext(
      realm.owner,
      getProgramVersionForRealm(realmInfo),
      wallet,
      connection,
      endpoint
    )

    const nativeAmount = new BN(
      new BigNumber(amount).shiftedBy(depositMintInfo.decimals).toString()
    )

    try {
      await voteRegistryDepositWithoutLockup({
        rpcContext,
        fromPk: realmTokenAccount.publicKey,
        mintPk: realm.account.communityMint,
        realmPk: realm.pubkey,
        programId: realm.owner,
        programVersion: realmInfo?.programVersion,
        amount: nativeAmount,
        tokenOwnerRecordPk,
        client,
        communityMintPk: realm.account.communityMint,
      })
      await getOwnedDeposits({
        realmPk: realm.pubkey,
        communityMintPk: realm.account.communityMint,
        walletPk: wallet.publicKey,
        client: client,
        connection,
      })
      await queryClient.invalidateQueries(
        tokenAccountQueryKeys.byOwner(connection.rpcEndpoint, wallet.publicKey)
      )

      setIsOpen(false)
    } catch (e) {
      console.log(e)
      notify({ message: `Something went wrong ${e}`, type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    depositTokens,
    depositMintInfo,
    isLoading,
  }
}
